/* eslint-disable import/no-anonymous-default-export */

/**
 * database configuration
 * ----------------------------------------
 * Format matches feature config modules so it is auto-collected by your existing
 * Webpack loader (require.context) alongside other feature configs.
 *
 * Version semantics (store.object.data): "MAJOR.MINOR.PATCH"
 * - MAJOR (store): Dexie store definition change → requires DB version bump.
 * - MINOR (object): object model changes → coerce existing records.
 * - PATCH (data): data-only adjustments (upserts) → no schema change.
 */

const VERSION = "1.1.1";

/**
 * modelStore Configuration Object
 *
 * Represents a metadata record for tracking store configuration state.
 * This object serves as a special meta-row that stores versioning and
 * descriptive information about the store itself.
 *
 * @typedef {Object} ModelStore
 *
 * @property {string} id
 *   Unique identifier for the store record.
 *   MUST be set to "__history" for the store's own metadata row.
 *   This special ID distinguishes the meta record from regular data entries.
 *
 * @property {string} label
 *   Human-readable display name for the store.
 *   Example: "History Meta", "Configuration Store"
 *   Used for UI display and logging purposes.
 *
 * @property {string} description
 *   Detailed documentation or notes about the store's purpose.
 *   Can include usage instructions, schema notes, or other metadata.
 *
 * @property {string} version
 *   Version identifier for the current configuration.
 *   Represents the version string of this config once it has been applied.
 *   Useful for tracking schema changes and migrations.
 *
 * @property {number} created
 *   Unix timestamp (milliseconds) of when this record was created.
 *   Defaults to Date.now() at initialization.
 *
 * @property {number} modified
 *   Unix timestamp (milliseconds) of the last modification.
 *   Should be updated via Date.now() whenever the record changes.
 *
 * @example
 * export const modelStore = {
 *   id: "__history",
 *   label: "History Meta",
 *   description: "Tracks conversation history metadata and versioning",
 *   version: "1.1.1",
 *   created: 1699564800000,
 *   modified: 1699564800000,
 * };
 */
export const modelStore = {
  id: "", // MUST be "__history" for the store's own meta row
  label: "", // human-friendly label (e.g., "History Meta")
  description: "", // documentation/notes
  version: "", // the version string of this config once applied
  created: Date.now(),
  modified: Date.now(),
};

/**
 * modelData Configuration Object
 *
 * Represents a single migration or version transition step in the store's history.
 * Each instance tracks one configuration change, including what versions were
 * involved, when it was applied, and the outcome of the migration attempt.
 *
 * @typedef {Object} ModelData
 *
 * @property {string} id
 *   Unique identifier for this migration step.
 *   Format: `${store}@${prevVersion}->${newVersion}@${ts}`
 *   Example: "userStore@1.1.1->1.1.2@1699564800000"
 *   Combines store name, version transition, and timestamp for uniqueness.
 *
 * @property {string} store
 *   Name or identifier of the store being migrated.
 *   Example: "userStore", "settingsStore"
 *
 * @property {string} prevVersion
 *   Version identifier before applying this migration step.
 *   Represents the state of the store prior to this change.
 *   Example: "1.1.1", "2.3.1"
 *
 * @property {string} newVersion
 *   Version identifier after applying this migration step.
 *   This is the "target" or "chosen" version for this migration.
 *   Example: "1.1.1", "2.4.1"
 *
 * @property {string} bumpType
 *   Type of semantic version change.
 *   Valid values: "major" | "minor" | "patch"
 *   - "major": Breaking changes (e.g., 1.1.1 -> 2.1.1)
 *   - "minor": New features, backward compatible (e.g., 1.1.1 -> 1.2.1)
 *   - "patch": Bug fixes, backward compatible (e.g., 1.1.1 -> 1.2.1)
 *
 * @property {number|null} applied
 *   Unix timestamp (milliseconds) when this migration was applied.
 *   - null if the migration has not been applied yet
 *   - epoch milliseconds (from Date.now()) if applied
 *   Example: 1699564800000
 *
 * @property {string} stepFile
 *   File path of the configuration step that defines this migration.
 *   Corresponds to cfg.filePath in the migration system.
 *   Example: "./migrations/001-add-user-fields.js"
 *
 * @property {string} fileVersion
 *   Version string found in the step file.
 *   Used to track which file version was used for this step.
 *
 * @property {string} objectVersion
 *   Version property from the configuration object (cfg.version).
 *   May differ from fileVersion if the config has metadata about versioning.
 *
 * @property {string} decision
 *   Outcome of the migration attempt.
 *   Valid values: "migrate" | "skipped" | "blocked"
 *   - "migrate": Migration was successfully applied
 *   - "skipped": Migration was intentionally skipped (see reason)
 *   - "blocked": Migration was prevented from running (see reason)
 *
 * @property {string} reason
 *   Optional explanation for why a migration was skipped or blocked.
 *   Empty string if decision is "migrate".
 *   Examples: "Already at target version", "Missing dependencies", "User opted out"
 *
 * @example
 * // Successful migration
 * export const modelData = {
 *   id: "userStore@1.1.1->1.1.2@1699564800000",
 *   store: "userStore",
 *   prevVersion: "1.1.1",
 *   newVersion: "1.1.2",
 *   bumpType: "minor",
 *   applied: 1699564800000,
 *   stepFile: "./migrations/001-add-email-field.js",
 *   fileVersion: "1.1.1",
 *   objectVersion: "1.1.1",
 *   decision: "migrate",
 *   reason: "",
 * };
 *
 * @example
 * // Skipped migration
 * export const modelData = {
 *   id: "userStore@1.1.1->2.1.1@1699651200000",
 *   store: "userStore",
 *   prevVersion: "1.1.1",
 *   newVersion: "2.1.1",
 *   bumpType: "major",
 *   applied: null,
 *   stepFile: "./migrations/002-breaking-schema-change.js",
 *   fileVersion: "2.1.1",
 *   objectVersion: "2.1.1",
 *   decision: "skipped",
 *   reason: "User chose to stay on current version",
 * };
 */
export const modelData = {
  id: "", // e.g., `${store}@${prevVersion}->${newVersion}@${ts}`
  store: "",
  prevVersion: "", // version before applying this step
  newVersion: "", // version applied for this step (the "chosen" version)
  bumpType: "", //"major"|"minor"|"patch",
  applied: null, // epoch ms
  stepFile: "", // cfg.filePath
  fileVersion: "",
  objectVersion: "", // cfg.version
  decision: "", //"migrate"|"skipped"|"blocked", // outcome
  reason: "", // why skipped/blocked (optional)
};

/**
 *
 * Migration Configuration Object
 *
 * Defines a single migration step for a store, including version information,
 * schema changes, data updates, and transformation hooks. Each configuration
 * file represents one version increment (MAJOR, MINOR, or PATCH).
 *
 * @typedef {Object} MigrationConfig
 *
 * ============================================================================
 * VERSION MANAGEMENT
 * ============================================================================
 *
 * @property {string} version
 *   REQUIRED. Semantic version string (MAJOR.MINOR.PATCH).
 *   Must match the version in the config file name.
 *
 *   Versioning Rules:
 *   - New stores start at 1.1.1
 *   - PATCH (position 3): Data object key values (1.1.1 -> 1.1.2)
 *   - MINOR (position 2): Model updates (1.1.x -> 1.2.1)
 *   - MAJOR (position 1): Store schema updates (1.x.x -> 2.1.1)
 *
 *   Constraints:
 *   - Cannot skip versions (1.1.1 cannot jump to 1.1.5)
 *   - After incrementing MINOR, PATCH resets to 1
 *   - After incrementing MAJOR, both MINOR and PATCH reset to 1
 *
 *   Examples:
 *   - 1.1.1 -> 1.1.2 ✓ (patch increment)
 *   - 1.1.2 -> 1.2.1 ✓ (minor increment, patch resets)
 *   - 1.2.7 -> 2.1.1 ✓ (major increment, both reset)
 *   - 1.1.1 -> 1.1.5 ✗ (skips versions 1.1.2-1.1.4)
 *   - 1.1.2 -> 1.2.2 ✗ (patch should reset to 1)
 *
 * @property {boolean} forceUpdate
 *   Controls whether the user can skip this migration.
 *   - true: Skip user prompt, apply update automatically
 *   - false: Allow user to decline the update
 *
 *   Future Behavior:
 *   When true, will prompt user to backup before forcing the update.
 *
 * @property {string} type
 *   Human-readable update type indicator: "MAJOR" | "MINOR" | "PATCH"
 *   For convenience and quick reference only.
 *   Not used in code to determine update behavior.
 *
 * ============================================================================
 * DOCUMENTATION
 * ============================================================================
 *
 * @property {string[]} changeLog
 *   Array of specific technical changes included in this config.
 *   Intended for developer documentation and internal tracking.
 *
 *   Examples:
 *   - "Removing description index"
 *   - "Adding email field to user model"
 *   - "Migrating data from oldField to newField"
 *
 * @property {string} changeDescription
 *   User-facing description of the update.
 *   Displayed to users when prompted to apply the migration.
 *   Should be clear and non-technical.
 *
 *   Example:
 *   "This update improves search performance by optimizing the index structure."
 *
 * ============================================================================
 * STORE SCHEMA (MAJOR UPDATES)
 * ============================================================================
 *
 * @property {Object} stores
 *   Defines the IndexedDB store schema and indexes.
 *
 *   Rules:
 *   - Only ONE store definition per config file
 *   - Additional stores will be ignored
 *   - Store modifications require a MAJOR version bump
 *   - Modifying indexes (add/remove) requires a new MAJOR update config
 *
 *   Format:
 *   The key is the store name, the value is a comma-separated string of indexes.
 *   First index is the keyPath, remaining are indexed fields.
 *
 *   @example
 *   stores: {
 *     settings: "id,modified,category"
 *     // "id" = keyPath
 *     // "modified" and "category" = indexed fields for queries
 *   }
 *
 *   Index Modifications:
 *   - Add index: Include new index in string, create MAJOR config
 *   - Remove index: Exclude index from string, create MAJOR config
 *
 * ============================================================================
 * MODEL UPDATES (MINOR UPDATES)
 * ============================================================================
 *
 * @property {Object} model
 *   Defines the data models for store objects and data objects.
 *
 * @property {Object} model.storeObject
 *   Model for the single store metadata object.
 *   This is the special object with id "__history" that tracks store state.
 *
 *   Model Modifications:
 *   - Add keys: Include new key in model, create MINOR config
 *   - Remove keys: Remove key from model, create MINOR config
 *
 * @property {Object} model.dataObject
 *   Model for all data objects in the store (excluding storeObject).
 *   All data rows will conform to this model.
 *
 *   Model Modifications:
 *   - Add keys: Include new key in model, create MINOR config
 *   - Remove keys: Remove key from model AND configure transform hook
 *     with keyPruning to remove the keys from existing data
 *     (keys are not removed automatically)
 *
 *   @example
 *   model: {
 *     storeObject: {
 *       id: "__history",
 *       version: "1.2.1",
 *       created: Date.now(),
 *       modified: Date.now()
 *     },
 *     dataObject: {
 *       id: "",
 *       name: "",
 *       email: "", // newly added field
 *       created: Date.now(),
 *       modified: Date.now()
 *     }
 *   }
 *
 * ============================================================================
 * DATA POPULATION (PATCH UPDATES)
 * ============================================================================
 *
 * @property {Array} data
 *   Array of data objects to add to the store during this update.
 *   Empty array if no data needs to be added.
 *
 *   Initial Store Setup (v1.1.1):
 *   - Optionally seed the storeObject to lock starting versions
 *   - If not seeded, fallback creates object from model with version
 *     and timestamps
 *
 *   Each object in the array should conform to the dataObject model.
 *
 *   @example
 *   data: [
 *     {
 *       id: "default-1",
 *       name: "Default Setting",
 *       value: true,
 *       created: 1699564800000,
 *       modified: 1699564800000
 *     }
 *   ]
 *
 * ============================================================================
 * TRANSFORMATION HOOKS
 * ============================================================================
 *
 * @property {Object} hooks
 *   Optional transformation and lifecycle hooks.
 *   Hooks apply to data objects only, never to the storeObject/meta row.
 *
 * @property {Function} [hooks.transform]
 *   Transforms each data object during migration.
 *
 *   @param {Object} obj - The data object being transformed
 *   @param {Object} ctx - Context object
 *   @param {string} ctx.stepVersion - The version of this migration step
 *   @param {Object} ctx.model - The target dataObject model
 *   @param {Object} ctx.utils - Utility functions
 *   @param {Function} ctx.utils.keyPruning - Removes keys not in model
 *   @returns {Object} The transformed object
 *
 *   Common Use Cases:
 *   - Remove deprecated fields with keyPruning
 *   - Transform field values
 *   - Migrate data from old structure to new
 *
 *   @example
 *   transform: (obj, ctx) => {
 *     console.debug(`[settings@${ctx.stepVersion}] transform`, Object.keys(obj));
 *
 *     // Remove fields no longer in the model
 *     ctx.utils.keyPruning(obj, ctx.model);
 *
 *     // Transform a field
 *     if (obj.oldField) {
 *       obj.newField = obj.oldField.toUpperCase();
 *       delete obj.oldField;
 *     }
 *
 *     return obj;
 *   }
 *
 * @property {Function} [hooks.beforePopulate]
 *   Runs before data population begins.
 *
 *   @param {Object} params
 *   @param {IDBDatabase} params.db - IndexedDB database instance
 *   @param {string} params.store - Store name
 *   @param {string} params.version - Migration version
 *   @returns {Promise<void>}
 *
 *   Use Cases:
 *   - Prepare data structures
 *   - Validate preconditions
 *   - Set up temporary state
 *
 * @property {Function} [hooks.afterPopulate]
 *   Runs after data population completes.
 *
 *   @param {Object} params
 *   @param {IDBDatabase} params.db - IndexedDB database instance
 *   @param {string} params.store - Store name
 *   @param {string} params.version - Migration version
 *   @returns {Promise<void>}
 *
 *   Use Cases:
 *   - Clean up temporary data
 *   - Trigger post-migration actions
 *   - Validate migration success
 */
export default {
  /**
   * version  MAJOR.MINOR.PATCH
   *
   * For new store, set version at 1.1.1
   *
   * To apply patch data object key values, increment position 3 (1.1.2, so 1.1.1 -> 1.1.2)
   * To apply minor "model" updates, increment position 2 (1.2.1, so 1.1.1 || 1.1.2 || 1.1.6 -> 1.2.1)
   * To apply MAJOR "store" updates, increment postion 1 (2.1.1, so 1.2.1 || 1.2.7 || 1.9.4 -> 2.1.1)
   *
   * Versions cannot jump sequence
   *   (e.g. ver 1.1.1 cannot jump to 1.1.5, ver 1.1.2 cannot jump to 1.2.2, it must go to 1.2.1)
   *
   * @property {string} version - REQUIRED Version used in update and must match version in file name
   */
  version: VERSION,

  /**
   * forceUpdate
   *
   * to be used when updates are required not optional by user
   *
   * true  this will skip the user prompt
   * false this will allow user to skip update
   *
   * ** FUTURE behavior will be to prompt user to backup first and then force the update
   *
   * @property {boolean} forceUpdate - force update without allowing user to skip
   */
  forceUpdate: true,

  /**
   * type
   *
   * Type options (MAJOR || MINOR || PATCH)
   *
   * This value is for convenience and quick reference and is not used to determine any update decisions in code
   *
   * @property {string} type - Human-readable indication representing the version number update type MAJOR.MINOR.PATCH
   * */
  type: "MAJOR", // major because it is the initial version

  /**
   * changeLog
   *
   * Intended for documentation purposes but could be used for other purposes as well
   *
   * @property {string[]} changeLog - Array of specific changes this config contains
   */
  changeLog: [
    "- create store and indexes",
    "- establish store and data models",
    "- populate store object",
  ],

  /**
   * changeDescription
   *
   * Description of changes in this config update. Intended to be informative to app user
   *  if prompted to update
   *
   * @property {string} changeDescription - Public description of update
   */
  changeDescription: "Initiate system store.",

  /**
   * stores (MAJOR)
   *
   * ONLY supports a single store defined per config, new store = new config file
   *  additional configured stores will be ignored
   *
   * Store modifications require a database version bump
   *
   *  + to modify indexes (adding), add index configuration, create a MAJOR update config file
   *  - to modify indexes (removing), remove index, leaving other indexes in place, crate MAJOR update config file
   *
   * @constant {Object} stores Accepts only one store and schema definition
   */
  stores: {
    __history: "id,store,applied,newVersion,prevVersion", // migration log
  },

  /**
   * Models (MINOR)
   *
   * Minor updates will make configured change to ALL data objects
   * For storeObject
   *  + to modify model (adding keys), add key and create a MINOR update config file
   *  - to modify model (removing keys), remove key leaving other keys
   *
   * For dataObject
   *  + to modify model (adding keys), add key and create a MINOR update config file
   *  - to modify model (removing keys), remove key leaving other keys and configure
   *    transform hook to run keyPruning. Keys are not removed automatically
   *
   * @constant {Object} storeObject Defines model for single store object
   * @constant {Object} dataObject Defines model for all data objects
   */
  model: {
    storeObject: modelStore,
    dataObject: modelData,
  },

  /**
   * data (PATCH)
   *
   * When store is initiated (v1.1.1)
   * - Seed the storeObject row in data for this store if you want to lock their starting versions.
   * - Fallback will create an object using model and will only apply version and date/time stamps
   *
   * @property {Array.<{...dataObject}>} data define data items to add to store for this patch update
   *    if no data needs to be added, keep array empty
   */
  data: [
    // The store's own meta row (id must equal the store name)
    {
      id: "__history",
      label: "History Meta",
      description: "Tracks history of applied store updates.",
      version: VERSION,
      created: Date.now(),
      modified: Date.now(),
    },
  ],

  /**
   * Hooks
   *
   * - hooks are for data objects and do not apply to storeObjects
   */
  hooks: {
    // Will run for *data rows only* (never for the storeObject/meta row)
    // transform: (obj, ctx) => {
    //   console.debug(
    //     `[settings@${ctx.stepVersion}] transform`,
    //     Object.keys(obj)
    //   );
    //   // Only prune in *this* version, e.g. when you removed fields
    //   ctx.utils.keyPruning(obj, ctx.model);
    //   return obj;
    // },
    // beforePopulate: async ({ db, store, version }) => {},
    // afterPopulate: async ({ db, store, version }) => {},
  },
};
/* eslint-enable import/no-anonymous-default-export */
